import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { useSensor, useSensors } from '@dnd-kit/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { canMoveTripListRowToAnotherDay, type TripListMoveDayOption } from './tripListMoveDayOptions'
import type { TripListGroup, TripListRow } from './tripListTypes'
import type {
  DropTarget,
  PointerCoordinates,
  TripListRenderableGroup,
  TripListRenderableRow,
} from './tripListReorder'
import {
  POINTER_DRAG_ACTIVATION_DISTANCE,
  TOUCH_DRAG_ACTIVATION_DELAY,
  TOUCH_DRAG_ACTIVATION_TOLERANCE,
  TripListPointerSensor,
  TripListTouchSensor,
  buildOptimisticTripListRenderableGroups,
  buildOrderedTripListRenderableGroups,
  buildTripListRenderableGroups,
  getDropPlacementForRowTarget,
  getGroupMoveDayOption,
  getTripListDropTargetFromPoint,
  isSameDropTarget,
  isTripListDropTargetAvailable,
  moveItemToDropTarget,
  moveItemToGroupTarget,
} from './tripListReorder'
import { useDragPointerTracking } from '../useDragPointerTracking'

interface UseTripListDragReorderParams {
  groups: Array<TripListGroup>
  visibleRows: Array<TripListRow>
  selectedRowIds: Set<string>
  bookingRowId: string | undefined
  noteUpdateRowId: string | undefined
  statusUpdateRowId: string | undefined
  dragActionsDisabled: boolean
  onMoveToDay: (row: TripListRow, option: TripListMoveDayOption) => void | boolean | Promise<void | boolean>
}

interface UseTripListDragReorderResult {
  orderedRenderableGroups: Array<TripListRenderableGroup>
  activeRenderableRow: TripListRenderableRow | undefined
  overDragRowId: string | undefined
  dropTargetPlacement: 'before' | 'after' | undefined
  sensors: ReturnType<typeof useSensors>
  handleDragStart: (event: DragStartEvent) => void
  handleDragMove: (event: DragMoveEvent) => void
  handleDragEnd: (event: DragEndEvent) => void
  handleDragCancel: () => void
}

export function useTripListDragReorder({
  groups,
  visibleRows,
  selectedRowIds,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  dragActionsDisabled,
  onMoveToDay,
}: UseTripListDragReorderParams): UseTripListDragReorderResult {
  const [orderedRowIds, setOrderedRowIds] = useState<Array<string>>([])
  const [rowGroupIdOverrideById, setRowGroupIdOverrideById] = useState<Map<string, string>>(() => new Map())
  const [activeRowId, setActiveRowId] = useState<string | undefined>()
  const [overDragRowId, setOverDragRowId] = useState<string | undefined>()
  const [dropTargetPlacement, setDropTargetPlacement] = useState<'before' | 'after' | undefined>()
  const [isDragActive, setIsDragActive] = useState(false)
  const activeDragRowIdRef = useRef<string | undefined>(undefined)
  const dropTargetRef = useRef<DropTarget | undefined>(undefined)
  const canonicalVisibleRowIdsRef = useRef<Array<string>>([])
  const moveSequenceRef = useRef(0)

  const renderableGroups = useMemo(
    () => buildTripListRenderableGroups(groups, selectedRowIds, bookingRowId, noteUpdateRowId, statusUpdateRowId),
    [bookingRowId, groups, noteUpdateRowId, selectedRowIds, statusUpdateRowId],
  )
  const visibleRowIds = useMemo(() => visibleRows.map(row => row.id), [visibleRows])
  const canonicalGroupIdByRowId = useMemo(() => {
    const entries = groups.flatMap(group => group.rows.map(row => [row.id, group.id] as const))
    return new Map(entries)
  }, [groups])
  const rowOrderIndexById = useMemo(() => {
    const orderedIds = orderedRowIds.length > 0 ? orderedRowIds : visibleRowIds
    return new Map(orderedIds.map((rowId, index) => [rowId, index]))
  }, [orderedRowIds, visibleRowIds])
  const optimisticRenderableGroups = useMemo(() => {
    return buildOptimisticTripListRenderableGroups(renderableGroups, rowGroupIdOverrideById)
  }, [renderableGroups, rowGroupIdOverrideById])
  const orderedRenderableGroups = useMemo(() => {
    return buildOrderedTripListRenderableGroups(optimisticRenderableGroups, rowOrderIndexById)
  }, [optimisticRenderableGroups, rowOrderIndexById])
  const renderableRowById = useMemo(() => {
    return new Map(orderedRenderableGroups.flatMap(group => group.rows.map(row => [row.row.id, row] as const)))
  }, [orderedRenderableGroups])
  const activeRenderableRow = activeRowId ? renderableRowById.get(activeRowId) : undefined
  const rowById = useMemo(() => {
    return new Map(Array.from(renderableRowById.entries()).map(([rowId, row]) => [rowId, row.row] as const))
  }, [renderableRowById])
  const groupById = useMemo(() => new Map(orderedRenderableGroups.map(group => [group.id, group])), [orderedRenderableGroups])
  const groupIdByRowId = useMemo(() => {
    const entries = orderedRenderableGroups.flatMap(group => group.rows.map(row => [row.row.id, group.id] as const))
    return new Map(entries)
  }, [orderedRenderableGroups])

  const sensors = useSensors(
    useSensor(TripListPointerSensor, {
      activationConstraint: {
        distance: POINTER_DRAG_ACTIVATION_DISTANCE,
      },
    }),
    useSensor(TripListTouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_ACTIVATION_DELAY,
        tolerance: TOUCH_DRAG_ACTIVATION_TOLERANCE,
      },
    }),
  )

  useEffect(() => {
    const canonicalVisibleRowIds = canonicalVisibleRowIdsRef.current
    const visibleRowState = visibleRows.map(row => `${row.id}:${canonicalGroupIdByRowId.get(row.id) ?? ''}`)
    const canonicalOrderChanged = (
      canonicalVisibleRowIds.length !== visibleRowState.length ||
      canonicalVisibleRowIds.some((rowId, index) => rowId !== visibleRowState[index])
    )

    canonicalVisibleRowIdsRef.current = visibleRowState
    if (!canonicalOrderChanged) return

    setRowGroupIdOverrideById(prev => prev.size === 0 ? prev : new Map())
    setOrderedRowIds(prev => {
      if (
        prev.length === visibleRowIds.length &&
        prev.every((rowId, index) => rowId === visibleRowIds[index])
      ) {
        return prev
      }

      return visibleRowIds
    })
  }, [canonicalGroupIdByRowId, visibleRowIds, visibleRows])

  const updateDropTargetFromPoint = useCallback((dropPoint: PointerCoordinates) => {
    const rawDropTarget = getTripListDropTargetFromPoint(dropPoint, activeDragRowIdRef.current)
    const activeRow = activeDragRowIdRef.current ? rowById.get(activeDragRowIdRef.current) : undefined
    const nextDropTarget = rawDropTarget && activeRow && isTripListDropTargetAvailable(rawDropTarget, activeRow, rowById, groupById) ?
      rawDropTarget :
      undefined
    if (isSameDropTarget(nextDropTarget, dropTargetRef.current)) return

    dropTargetRef.current = nextDropTarget
    setOverDragRowId(nextDropTarget?.type === 'row' ? nextDropTarget.rowId : undefined)
    setDropTargetPlacement(nextDropTarget?.type === 'row' ? nextDropTarget.placement : undefined)
  }, [groupById, rowById])

  const { startTracking, stopTracking, getDropPoint } = useDragPointerTracking({
    isDragActive,
    onDropPointChange: updateDropTargetFromPoint,
  })

  const resetDragState = useCallback(() => {
    setActiveRowId(undefined)
    setOverDragRowId(undefined)
    setDropTargetPlacement(undefined)
    setIsDragActive(false)
    activeDragRowIdRef.current = undefined
    dropTargetRef.current = undefined
    stopTracking()
  }, [stopTracking])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeRowId = String(event.active.id)
    const activeRow = renderableRowById.get(activeRowId)
    if (!activeRow || activeRow.row.isBooked) {
      resetDragState()
      return
    }

    setOverDragRowId(undefined)
    setDropTargetPlacement(undefined)
    setIsDragActive(true)
    setActiveRowId(activeRowId)
    activeDragRowIdRef.current = activeRowId
    dropTargetRef.current = undefined
    startTracking(event)
  }, [renderableRowById, resetDragState, startTracking])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const dropPoint = getDropPoint(event)
    if (!dropPoint) return

    updateDropTargetFromPoint(dropPoint)
  }, [getDropPoint, updateDropTargetFromPoint])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dropPoint = getDropPoint(event)
    const activeRowId = String(event.active.id)
    const resolvedDropTarget = dropPoint ? getTripListDropTargetFromPoint(dropPoint, activeRowId) : undefined
    resetDragState()
    if (dragActionsDisabled) return

    const dropTarget = resolvedDropTarget
    const activeRow = rowById.get(activeRowId)
    if (!activeRow) return
    if (activeRow.isBooked) return

    if (dropTarget && !isTripListDropTargetAvailable(dropTarget, activeRow, rowById, groupById)) return
    if (!canMoveTripListRowToAnotherDay(activeRow)) return

    let targetOption: TripListMoveDayOption | undefined
    const previousRowGroupIdOverrideById = rowGroupIdOverrideById
    const previousOrderedRowIds = orderedRowIds
    if (dropTarget?.type === 'group') {
      const group = groupById.get(dropTarget.groupId)
      targetOption = group ? getGroupMoveDayOption(group, dropTarget.placement) : undefined
      if (group) {
        if (groupIdByRowId.get(activeRowId) !== group.id) {
          setRowGroupIdOverrideById(prev => new Map(prev).set(activeRowId, group.id))
        }
        setOrderedRowIds(prev => {
          const sourceIds = prev.length > 0 ? prev : visibleRowIds
          return moveItemToGroupTarget(sourceIds, activeRowId, group.rows.map(row => row.row.id), dropTarget.placement)
        })
      }
    } else if (dropTarget?.type === 'row') {
      const overRow = rowById.get(dropTarget.rowId)
      if (!overRow) return
      const activeGroupId = groupIdByRowId.get(activeRowId)
      const rowOverGroupId = groupIdByRowId.get(dropTarget.rowId)

      if (activeRowId !== dropTarget.rowId) {
        if (rowOverGroupId && activeGroupId !== rowOverGroupId) {
          setRowGroupIdOverrideById(prev => new Map(prev).set(activeRowId, rowOverGroupId))
        }
        setOrderedRowIds(prev => {
          const sourceIds = prev.length > 0 ? prev : visibleRowIds
          return moveItemToDropTarget(sourceIds, activeRowId, dropTarget.rowId, dropTarget.placement)
        })
      }

      const placement = getDropPlacementForRowTarget(activeRowId, dropTarget, overRow)
      targetOption = overRow.day ? {
        id: `day:${overRow.day.id}`,
        title: overRow.dayLabel,
        subtitle: overRow.stop.placeName,
        selected: false,
        target: { targetType: 'day', dayId: overRow.day.id },
        placement,
      } : {
        id: 'unplanned',
        title: overRow.dayLabel,
        subtitle: overRow.stop.placeName,
        selected: false,
        target: { targetType: 'unplanned' },
        placement,
      }
    }

    if (!targetOption) return

    // Tag this move so a late failure only rolls back if no newer drag has since
    // applied its own optimistic state, otherwise the stale snapshot would clobber it.
    const moveToken = ++moveSequenceRef.current
    const rollback = () => {
      if (moveSequenceRef.current !== moveToken) return
      setRowGroupIdOverrideById(previousRowGroupIdOverrideById)
      setOrderedRowIds(previousOrderedRowIds)
    }

    const moveResult = onMoveToDay(activeRow, targetOption)
    Promise.resolve(moveResult).then(moved => {
      if (moved === false) rollback()
    }).catch(rollback)
  }, [dragActionsDisabled, getDropPoint, groupById, groupIdByRowId, onMoveToDay, orderedRowIds, resetDragState, rowById, rowGroupIdOverrideById, visibleRowIds])

  const handleDragCancel = useCallback(() => {
    resetDragState()
  }, [resetDragState])

  return {
    orderedRenderableGroups,
    activeRenderableRow,
    overDragRowId,
    dropTargetPlacement,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  }
}
